import React, { useState, useEffect, useRef, useCallback, useId } from "react";
import { useClickOutside } from "../../../hooks/useClickOutside";

export interface ProjectOption {
  id: string;
  name: string;
  operator?: {
    name: string;
    shortName?: string | null;
  } | null;
  competition?: {
    name: string;
    code?: string | null;
  } | null;
}

export interface ProjectAutocompleteProps {
  /** List of available projects to select from */
  projects: ProjectOption[];
  /** Currently selected project ID (empty string if none) */
  value: string;
  /** Callback invoked when selected project changes */
  onChange: (value: string) => void;
  /** Whether to display error styling (red border) */
  hasError?: boolean;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Whether project data is currently loading */
  isLoading?: boolean;
  /** Optional id applied to the trigger button, so an external <label> can point at it via htmlFor */
  triggerId?: string;
  /** Optional id of an external <label> element, wired onto the trigger via aria-labelledby */
  ariaLabelledBy?: string;
}

const operatorLabel = (project: ProjectOption): string =>
  project.operator?.shortName || project.operator?.name || "";

const competitionLabel = (project: ProjectOption): string =>
  project.competition?.name || project.competition?.code || "";

/**
 * Searchable select for operator projects.
 *
 * A plain <select> is unusable here: there are hundreds of projects and the only
 * way to tell them apart is the operator behind them. Mirrors the existing
 * OperatorAutocomplete/CompetitionAutocomplete pattern (client-side filter).
 *
 * Keyboard/ARIA: implements the "listbox popup" combobox pattern (mirrors
 * BarometerOccupationFilter) — focus stays on the search input while open;
 * ArrowUp/ArrowDown/Home/End move a virtual highlight communicated via
 * aria-activedescendant, Enter selects it, Escape closes and returns focus
 * to the trigger. The panel also closes on focusout (Tab away), independent
 * of useClickOutside's mousedown-only listener.
 */
export const ProjectAutocomplete: React.FC<ProjectAutocompleteProps> = ({
  projects,
  value,
  onChange,
  hasError = false,
  disabled = false,
  isLoading = false,
  triggerId,
  ariaLabelledBy,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionElRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const listboxId = useId();

  const selectedProject = projects.find((project) => project.id === value);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const closePanel = useCallback(() => {
    setIsOpen(false);
    setSearchQuery("");
    setHighlightedIndex(-1);
  }, []);

  const handleClickOutside = useCallback(() => {
    closePanel();
  }, [closePanel]);

  useClickOutside(dropdownRef, handleClickOutside, isOpen);

  // Reset the virtual highlight whenever the filtered list changes (new search query).
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchQuery]);

  const filteredProjects = projects.filter((project) => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;

    return [
      project.name,
      project.operator?.name,
      project.operator?.shortName,
      project.competition?.name,
      project.competition?.code,
    ].some((field) => field?.toLowerCase().includes(query));
  });

  // Scroll the highlighted option into view when navigating with arrow keys.
  useEffect(() => {
    if (highlightedIndex < 0) return;
    optionElRefs.current[highlightedIndex]?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  const highlightText = (text: string, query: string) => {
    if (!query.trim() || !text) return text;

    const parts = text.split(new RegExp(`(${query})`, "gi"));
    return (
      <>
        {parts.map((part, index) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <mark key={index} className="bg-yellow-200 font-semibold">
              {part}
            </mark>
          ) : (
            <span key={index}>{part}</span>
          )
        )}
      </>
    );
  };

  const handleProjectClick = (projectId: string) => {
    onChange(projectId);
    closePanel();
  };

  const handleClear = () => {
    onChange("");
    closePanel();
  };

  const handleToggleOpen = () => {
    const willOpen = !isOpen;
    setIsOpen(willOpen);
    if (willOpen) {
      // Highlight the currently selected project (if any) so keyboard users
      // land somewhere sensible instead of an empty highlight.
      setHighlightedIndex(projects.findIndex((project) => project.id === value));
    }
  };

  const moveHighlight = (direction: 1 | -1) => {
    setHighlightedIndex((prev) => {
      if (filteredProjects.length === 0) return -1;
      if (prev === -1) {
        return direction === 1 ? 0 : filteredProjects.length - 1;
      }
      const next = prev + direction;
      if (next < 0) return filteredProjects.length - 1;
      if (next >= filteredProjects.length) return 0;
      return next;
    });
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        moveHighlight(1);
        break;
      case "ArrowUp":
        e.preventDefault();
        moveHighlight(-1);
        break;
      case "Home":
        e.preventDefault();
        setHighlightedIndex(filteredProjects.length > 0 ? 0 : -1);
        break;
      case "End":
        e.preventDefault();
        setHighlightedIndex(filteredProjects.length > 0 ? filteredProjects.length - 1 : -1);
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredProjects.length) {
          handleProjectClick(filteredProjects[highlightedIndex].id);
        }
        break;
      default:
        break;
    }
  };

  // Escape closes the panel regardless of which descendant currently has
  // focus (search input, clear button, "Wyczyść wybór"), and returns focus
  // to the trigger.
  const handleContainerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape" && isOpen) {
      e.stopPropagation();
      closePanel();
      triggerRef.current?.focus();
    }
  };

  // Close on focusout (e.g. Tab-ing away) — checked locally here instead of
  // extending the shared useClickOutside hook, which other components rely on.
  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!isOpen) return;
    const nextFocused = e.relatedTarget as Node | null;
    if (!nextFocused || !e.currentTarget.contains(nextFocused)) {
      closePanel();
    }
  };

  const triggerLabel = (): string => {
    if (isLoading) return "Ładowanie...";
    if (!selectedProject) return "Brak (wybierz projekt)";

    const operator = operatorLabel(selectedProject);
    return operator
      ? `${selectedProject.name} - ${operator}`
      : selectedProject.name;
  };

  const activeDescendantId =
    isOpen && highlightedIndex >= 0 ? `${listboxId}-option-${highlightedIndex}` : undefined;

  return (
    <div
      className="relative"
      ref={dropdownRef}
      onKeyDown={handleContainerKeyDown}
      onBlur={handleBlur}
    >
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        onClick={handleToggleOpen}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-labelledby={ariaLabelledBy}
        className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-left flex items-center justify-between ${
          hasError
            ? "border-red-500"
            : disabled
            ? "opacity-60 cursor-not-allowed border-neutral-300"
            : "border-neutral-300"
        }`}
      >
        <span
          className={`truncate ${
            selectedProject ? "text-neutral-900" : "text-neutral-500"
          }`}
        >
          {triggerLabel()}
        </span>
        <svg
          className={`w-4 h-4 ml-2 flex-shrink-0 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-full bg-white border border-neutral-200 rounded-lg shadow-lg z-[60] max-h-96 overflow-hidden flex flex-col">
          <div className="sticky top-0 bg-white border-b border-neutral-200 p-3">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                role="combobox"
                aria-expanded={isOpen}
                aria-autocomplete="list"
                aria-controls={listboxId}
                aria-activedescendant={activeDescendantId}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Szukaj projektu, operatora lub konkursu..."
                className="w-full pl-9 pr-8 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  aria-label="Wyczyść wyszukiwanie"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {value && (
              <button
                type="button"
                onClick={handleClear}
                className="w-full text-left px-4 py-3 hover:bg-neutral-50 transition-colors border-b border-neutral-100 text-primary-600 font-medium text-sm"
              >
                Wyczyść wybór
              </button>
            )}

            {filteredProjects.length === 0 ? (
              <div className="text-center text-neutral-500 py-4 text-sm">
                Nie znaleziono projektów
              </div>
            ) : (
              <div role="listbox" id={listboxId} aria-label="Lista projektów operatora">
                {filteredProjects.map((project, index) => {
                  const operator = operatorLabel(project);
                  const competition = competitionLabel(project);
                  const isHighlighted = index === highlightedIndex;
                  const isSelected = project.id === value;

                  return (
                    <button
                      key={project.id}
                      id={`${listboxId}-option-${index}`}
                      ref={(el) => {
                        optionElRefs.current[index] = el;
                      }}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      tabIndex={-1}
                      onClick={() => handleProjectClick(project.id)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      className={`w-full text-left px-4 py-3 hover:bg-neutral-50 transition-colors border-b border-neutral-100 last:border-b-0 ${
                        isHighlighted
                          ? "bg-primary-100"
                          : isSelected
                          ? "bg-primary-50"
                          : ""
                      }`}
                    >
                      <div className="font-medium text-sm text-neutral-900">
                        {highlightText(project.name, searchQuery)}
                      </div>
                      {(operator || competition) && (
                        <div className="text-xs text-neutral-600 mt-0.5">
                          {operator && highlightText(operator, searchQuery)}
                          {operator && competition ? " • " : ""}
                          {competition && highlightText(competition, searchQuery)}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectAutocomplete;
